import {
  Cancel,
  CancelRounded,
  CheckCircle,
  CloseRounded,
  CloudUploadOutlined,
  Delete,
  DeleteOutlined,
  InsertDriveFileOutlined,
  MoreVertOutlined,
  SearchSharp,
  SyncOutlined,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import * as XLSX from "xlsx";

import CustomerForm from "../../components/crm/CustomerForm";
import EmptyState from "../../components/crm/EmptyState";
import TableLoadingSkeleton, {
  type TableSkeletonColumn,
} from "../../components/crm/TableLoadingSkeleton";
import CustomConfirmDialog from "../../components/CustomConfirmDialog";
import CustomDialog from "../../components/CustomDialog";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import useCrmCustomers from "../../hooks/useCrmCustomers";
import {
  bulkUpdateCustomerStatus,
  deleteCustomer,
  multipleUpdatePoints,
  updateCustomer,
} from "../../services/crm.service";
import { formatThaiPhone } from "../../utils/phone";
import {
  Customer,
  CustomerFilters,
  CustomerMultipleUpdatePointsRequestData,
  CustomerUpdateRequest,
  UserStatus,
} from "../../types/crm";

const monthOptions = [
  { label: "All Month", value: "all" },
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const yearOptions = [
  { label: "2026", value: "2026" },
  { label: "2025", value: "2025" },
  { label: "2024", value: "2024" },
];

const CUSTOMERS_SKELETON_COLUMNS: TableSkeletonColumn[] = [
  { key: "checkbox", width: 50, isCheckbox: true },
  { key: "action", width: 80, align: "center" },
  { key: "no", width: 50, align: "center" },
  { key: "ecp-code", width: 120 },
  { key: "store-name", width: 150 },
  { key: "line-name", width: 130 },
  { key: "name-customer", width: 150 },
  { key: "email", width: 180 },
  { key: "phone-number", width: 120, align: "center" },
  { key: "line-verification", width: 110, align: "center" },
  { key: "points", width: 70, align: "center" },
  { key: "created-date", width: 120, align: "center" },
];

type CustomerListFilters = Omit<CustomerFilters, "statuses"> & {
  month: string;
  year: string;
  status: UserStatus | "all";
};

export default function CrmCustomers() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { showSnackbar } = useSnackbar();
  const [filters, setFilters] = useState<CustomerListFilters>({
    search: "",
    month: "all",
    year: "2026",
    status: "all",
    page: 0,
    page_size: 10,
  });

  useEffect(() => {
    setBreadcrumbs([{ label: "Customer List", to: "/crm/customers" }]);
  }, [setBreadcrumbs]);

  const apiFilters: CustomerFilters = {
    ...filters,
    statuses: filters.status === "all" ? undefined : [filters.status],
  };
  const { data, dataLoading, mutate } = useCrmCustomers(true, null, apiFilters);

  const customers: Customer[] = data?.data || [];
  const totalRecords = data?.total_records || 0;
  const isLoading = dataLoading;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [confirmMode, setConfirmMode] = useState<"single" | "bulk">("single");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [actionRow, setActionRow] = useState<Customer | null>(null);
  const [newStatus, setNewStatus] = useState("");
  const [changeStatusRemarkOpen, setChangeStatusRemarkOpen] = useState(false);
  const [changeStatusRemark, setChangeStatusRemark] = useState("");
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isImportSubmitting, setIsImportSubmitting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);

  const handleSearchChange = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, search: value, page: 0 }));
    setSelectedIds([]);
  }, []);

  const handleMonthChange = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, month: value, page: 0 }));
    setSelectedIds([]);
  }, []);

  const handleYearChange = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, year: value, page: 0 }));
    setSelectedIds([]);
  }, []);

  const handleStatusChange = useCallback((value: string) => {
    setFilters((prev) => ({
      ...prev,
      status: value as UserStatus | "all",
      page: 0,
    }));
    setSelectedIds([]);
  }, []);

  const handlePageChange = useCallback((_: unknown, page: number) => {
    setFilters((prev) => ({ ...prev, page }));
    setSelectedIds([]);
  }, []);

  const handlePageSizeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilters((prev) => ({
        ...prev,
        page_size: parseInt(event.target.value, 10),
        page: 0,
      }));
      setSelectedIds([]);
    },
    [],
  );

  // @ts-ignore
  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDialogOpen(true);
  };

  const handleView = (customer: Customer) => {
    navigate(`/crm/customers/${customer.id}`);
  };

  // const handleEdit = (customer: Customer) => { navigate(..); };

  // const handleDeleteClick = (customer: Customer) => { ... };

  const handleFormSubmit = async (formData: CustomerUpdateRequest) => {
    if (!selectedCustomer) return;
    setIsSubmitting(true);
    try {
      await updateCustomer(selectedCustomer.id, formData);
      setDialogOpen(false);
      mutate();
      showSnackbar("Customer updated successfully", "success");
    } catch (error) {
      console.error("Error saving customer:", error);
      showSnackbar("Failed to update customer", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setConfirmMode("bulk");
    setSelectedCustomer(null);
    setDeleteDialogOpen(true);
  };

  const handleBulkReject = () => {
    if (selectedIds.length === 0) return;
    setConfirmMode("bulk");
    setSelectedCustomer(null);
    setRejectRemark("");
    setRejectDialogOpen(true);
  };

  const handleBulkApprove = () => {
    if (selectedIds.length === 0) return;
    setConfirmMode("bulk");
    setSelectedCustomer(null);
    setNewStatus("active");
    setApproveDialogOpen(true);
  };

  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setIsDragActive(false);
    setImportError(null);
  };

  const parseImportFile = (
    file: File,
  ): Promise<CustomerMultipleUpdatePointsRequestData[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const workbook = XLSX.read(new Uint8Array(arrayBuffer), {
            type: "array",
          });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(
            sheet,
            { defval: "", raw: false },
          );
          if (rows.length > 100) {
            reject(new Error("File exceeds the maximum limit of 100 rows."));
            return;
          }
          const parsed: CustomerMultipleUpdatePointsRequestData[] = [];
          for (const row of rows) {
            const ecp_code = String(row["ecp_code"] ?? "").trim();
            const rawPoints = String(row["points"] ?? "").trim();
            const points = Number(rawPoints);
            const rawExpires = String(row["expires_at"] ?? "").trim();
            let expires_at: string | null = null;
            if (rawExpires) {
              const parsed_date = dayjs(rawExpires);
              if (parsed_date.isValid()) {
                expires_at = parsed_date.format("YYYY-MM-DD") + "T00:00:00Z";
              } else {
                reject(new Error(`Row with ecp_code "${ecp_code}" has an invalid expires_at value.`));
                return;
              }
            }
            if (!ecp_code) {
              reject(new Error("Each row must have an ecp_code value."));
              return;
            }
            if (!rawPoints || isNaN(points)) {
              reject(
                new Error(`Row with ecp_code "${ecp_code}" has an invalid points value.`),
              );
              return;
            }
            parsed.push({ ecp_code, points, expires_at: expires_at || null });
          }
          if (parsed.length === 0) {
            reject(new Error("The file contains no data rows."));
            return;
          }
          resolve(parsed);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file."));
      reader.readAsArrayBuffer(file);
    });
  };

  const SAMPLE_ROWS = [
    ["ecp_code", "points", "expires_at"],
    ["AA0001", 500, "2026-12-31"],
    ["AA0002", 200, "2026-06-30"],
    ["AA0003", 100, ""],
    ["BB1001", 1000, "2026-12-31"],
    ["BB1002", 750, "2026-09-30"],
  ];

  const handleDownloadSampleXlsx = () => {
    const worksheet = XLSX.utils.aoa_to_sheet(SAMPLE_ROWS);
    worksheet["!cols"] = [{ wch: 16 }, { wch: 10 }, { wch: 14 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Points");
    XLSX.writeFile(workbook, "sample_import_points.xlsx");
  };

  const handleDownloadSampleCsv = () => {
    const worksheet = XLSX.utils.aoa_to_sheet(SAMPLE_ROWS);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sample_import_points.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImportSubmit = async () => {
    if (!importFile) return;
    setIsImportSubmitting(true);
    setImportError(null);
    try {
      const parsedData = await parseImportFile(importFile);
      await multipleUpdatePoints(parsedData);
      handleCloseImportDialog();
      mutate();
      showSnackbar("Data Imported Successfully", "success");
    } catch (err: any) {
      const backendMsg =
        err?.response?.data?.message ||
        err?.response?.data?.stack ||
        (err instanceof Error ? err.message : "An unexpected error occurred.");
      setImportError(String(backendMsg));
    } finally {
      setIsImportSubmitting(false);
    }
  };

  const handleSelectImportFile = (file?: File) => {
    if (!file) return;
    const valid = /\.(csv|xlsx|xls)$/i.test(file.name);
    if (!valid) {
      setImportError("Invalid file. Please upload a .xlsx, .xls, or .csv file.");
      setImportFile(null);
      return;
    }
    setImportError(null);
    setImportFile(file);
  };

  const handleImportFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    handleSelectImportFile(event.target.files?.[0]);
  };

  const handleDropImportFile = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    handleSelectImportFile(event.dataTransfer.files?.[0]);
  };

  const handleDragOverImport = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeaveImport = () => {
    setIsDragActive(false);
  };

  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      const searchLower = (filters.search || "").toLowerCase();
      const matchesSearch =
        !searchLower ||
        customer.name.toLowerCase().includes(searchLower) ||
        customer.ecp_code.toLowerCase().includes(searchLower) ||
        customer.shop_name.toLowerCase().includes(searchLower) ||
        customer.line_name.toLowerCase().includes(searchLower) ||
        customer.email.toLowerCase().includes(searchLower) ||
        customer.phone.toLowerCase().includes(searchLower);

      const createdAt = dayjs(customer.created_at);
      const matchesMonth =
        filters.month === "all" || createdAt.format("MM") === filters.month;
      const matchesYear =
        filters.year === "all" || createdAt.format("YYYY") === filters.year;

      return matchesSearch && matchesMonth && matchesYear;
    });
  }, [filters, customers]);

  const pagedCustomers = filteredCustomers;

  const allSelected =
    pagedCustomers.length > 0 &&
    pagedCustomers.every((row) => selectedIds.includes(row.id));
  const isIndeterminate =
    selectedIds.length > 0 && !allSelected && pagedCustomers.length > 0;

  const selectedCustomers = useMemo(
    () => customers.filter((c) => selectedIds.includes(c.id)),
    [customers, selectedIds],
  );

  // Show Approve only if at least one selected customer is not already active
  const canApprove = useMemo(
    () => selectedCustomers.some((c) => c.status !== "active"),
    [selectedCustomers],
  );

  // Show Reject only if at least one selected customer is not already rejected
  const canReject = useMemo(
    () => selectedCustomers.some((c) => c.status !== "rejected"),
    [selectedCustomers],
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(pagedCustomers.map((row) => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleOpenActionMenu = (
    customer: Customer,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setActionAnchorEl(event.currentTarget);
    setActionRow(customer);
  };

  const handleCloseActionMenu = () => {
    setActionAnchorEl(null);
    setActionRow(null);
  };

  const handleActionView = () => {
    if (actionRow) handleView(actionRow);
    handleCloseActionMenu();
  };

  const handleActionDelete = () => {
    if (!actionRow) return;
    setSelectedCustomer(actionRow);
    setConfirmMode("single");
    setDeleteDialogOpen(true);
    handleCloseActionMenu();
  };

  const getChangeStatusOptions = (status?: string) => {
    switch (status) {
      case "inactive":
        return [
          { label: "Approve", value: "active", icon: <CheckCircle sx={{ fontSize: 20 }} /> },
          { label: "Reject", value: "rejected", icon: <Cancel sx={{ fontSize: 20 }} /> },
        ];
      case "active":
        return [
          { label: "In Monitor", value: "in_monitor", icon: <SyncOutlined sx={{ fontSize: 20 }} /> },
          { label: "Suspended", value: "suspended", icon: <SyncOutlined sx={{ fontSize: 20 }} /> },
        ];
      case "in_monitor":
        return [
          { label: "Active", value: "active", icon: <CheckCircle sx={{ fontSize: 20 }} /> },
          { label: "Suspended", value: "suspended", icon: <SyncOutlined sx={{ fontSize: 20 }} /> },
        ];
      case "suspended":
      case "rejected":
        return [
          { label: "Active", value: "active", icon: <CheckCircle sx={{ fontSize: 20 }} /> },
        ];
      default:
        return [];
    }
  };

  const handleActionStatusChange = (targetStatus: string) => {
    if (!actionRow) return;
    setSelectedCustomer(actionRow);
    setConfirmMode("single");
    setNewStatus(targetStatus);
    if (targetStatus === "rejected") {
      setRejectRemark("");
      setRejectDialogOpen(true);
    } else if (targetStatus === "in_monitor" || targetStatus === "suspended") {
      setChangeStatusRemark("");
      setChangeStatusRemarkOpen(true);
    } else {
      setApproveDialogOpen(true);
    }
    handleCloseActionMenu();
  };

  const handleConfirmChangeStatusWithRemark = async () => {
    if (!selectedCustomer?.id || !newStatus) return;
    setIsSubmitting(true);
    try {
      await updateCustomer(selectedCustomer.id, {
        status: newStatus as any,
        ...(changeStatusRemark.trim() ? { remark: changeStatusRemark } : {}),
      });
      setChangeStatusRemarkOpen(false);
      setSelectedCustomer(null);
      setNewStatus("");
      setChangeStatusRemark("");
      mutate();
      showSnackbar("Customer status updated successfully", "success");
    } catch (error) {
      console.error("Error changing status:", error);
      showSnackbar("Failed to change customer status", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmApprove = async () => {
    const targetStatus = newStatus || "active";
    setIsSubmitting(true);
    try {
      if (confirmMode === "single" && selectedCustomer) {
        await updateCustomer(selectedCustomer.id, { status: targetStatus as any });
      } else if (confirmMode === "bulk" && selectedIds.length > 0) {
        const approveIds = selectedCustomers
          .filter((c) => c.status !== "active")
          .map((c) => c.id);
        if (approveIds.length > 0) {
          await bulkUpdateCustomerStatus({
            customer_ids: approveIds,
            status: "active",
          });
        }
        setSelectedIds([]);
      }
      setApproveDialogOpen(false);
      setSelectedCustomer(null);
      setNewStatus("");
      mutate();
      showSnackbar("Customer status updated successfully", "success");
    } catch (error) {
      console.error("Error updating status:", error);
      showSnackbar("Failed to update customer status", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectRemark.trim()) return;
    setIsSubmitting(true);
    try {
      if (confirmMode === "single" && selectedCustomer) {
        await updateCustomer(selectedCustomer.id, {
          status: "rejected",
          remark: rejectRemark,
        });
      } else if (confirmMode === "bulk" && selectedIds.length > 0) {
        const rejectIds = selectedCustomers
          .filter((c) => c.status !== "rejected")
          .map((c) => c.id);
        if (rejectIds.length > 0) {
          await bulkUpdateCustomerStatus({
            customer_ids: rejectIds,
            status: "rejected",
            remark: rejectRemark,
          });
        }
        setSelectedIds([]);
      }
      setRejectDialogOpen(false);
      setSelectedCustomer(null);
      setRejectRemark("");
      mutate();
      showSnackbar("Customer(s) rejected successfully", "success");
    } catch (error) {
      console.error("Error rejecting:", error);
      showSnackbar("Failed to reject customer(s)", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsSubmitting(true);
    try {
      if (confirmMode === "single" && selectedCustomer) {
        await deleteCustomer(selectedCustomer.id);
      } else if (confirmMode === "bulk" && selectedIds.length > 0) {
        await Promise.all(selectedIds.map((id) => deleteCustomer(id)));
        setSelectedIds([]);
      }
      setDeleteDialogOpen(false);
      setSelectedCustomer(null);
      mutate();
      showSnackbar("Customer(s) deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting:", error);
      showSnackbar("Failed to delete customer(s)", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusOptions: Array<{
    label: string;
    value: CustomerListFilters["status"];
  }> = [
    { label: "All State Verification", value: "all" },
    { label: "Active", value: "active" },
    { label: "Pending Approval", value: "inactive" },
    { label: "In Monitor", value: "in_monitor" },
    { label: "Rejected", value: "rejected" },
    { label: "Suspended", value: "suspended" },
  ];

  const statusFilterChipConfig: Partial<
    Record<CustomerListFilters["status"], { label: string; bg: string; color: string }>
  > = {
    inactive: { label: "Pending Approval", bg: "#FFFBC5", color: "#BB6902" },
    in_monitor: { label: "In Monitor", bg: "#FFFBC5", color: "#BB6902" },
    active: { label: "Active", bg: "#E3F5E3", color: "#2D6830" },
    rejected: { label: "Reject", bg: "#FFE3E1", color: "#BE2217" },
    suspended: { label: "Suspended", bg: "#FFE3E1", color: "#BE2217" },
  };

  const renderStatusFilterOption = (
    status: CustomerListFilters["status"],
  ) => {
    if (status === "all") {
      return "All State Verification";
    }

    const config = statusFilterChipConfig[status];
    if (!config) {
      const matched = statusOptions.find((option) => option.value === status);
      return matched?.label || status;
    }

    return (
      <Chip
        size="small"
        label={config.label}
        sx={{
          height: 24,
          borderRadius: "4px",
          bgcolor: config.bg,
          color: config.color,
          "& .MuiChip-label": {
            px: "6px",
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1.4,
          },
        }}
      />
    );
  };

  const getVerificationChip = (status: UserStatus) => {
    const chipStyles = {
      height: 24,
      borderRadius: "4px",
      fontSize: 12,
      fontWeight: 400,
    };

    switch (status) {
      case "in_monitor":
        return (
          <Chip
            size="small"
            label="In Monitor"
            sx={{ ...chipStyles, bgcolor: "#FFFBC5", color: "#BB6902" }}
          />
        );
      case "active":
        return (
          <Chip
            size="small"
            label="Active"
            sx={{ ...chipStyles, bgcolor: "#E3F5E3", color: "#2D6830" }}
          />
        );
      case "rejected":
        return (
          <Chip
            size="small"
            label="Reject"
            sx={{ ...chipStyles, bgcolor: "#FFE3E1", color: "#BE2217" }}
          />
        );
      case "inactive":
        return (
          <Chip
            size="small"
            label="Pending Approval"
            sx={{ ...chipStyles, bgcolor: "#FFFBC5", color: "#BB6902" }}
          />
        );
      case "suspended":
        return (
          <Chip
            size="small"
            label="Suspended"
            sx={{ ...chipStyles, bgcolor: "#FFE3E1", color: "#BE2217" }}
          />
        );
      default:
        return (
          <Chip
            size="small"
            label="Unknown"
            sx={{ ...chipStyles, bgcolor: "#EDEEF1", color: "#6B7280" }}
          />
        );
    }
  };

  const selectedCount = selectedIds.length;

  const renderTable = () => (
    <>
      <TableContainer
        sx={{ borderTop: `1px solid ${theme.vars.palette.divider}` }}
      >
        <Table sx={{ minWidth: 1100, "& td, & th": { whiteSpace: "nowrap" } }}>
          <TableHead>
            <TableRow
              sx={{
                "& .MuiTableCell-root": {
                  height: 52,
                  borderBottom: `1px solid ${theme.vars.palette.divider}`,
                  color: "#25272C",
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  lineHeight: 1.4,
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={isIndeterminate}
                  onChange={(event) => handleSelectAll(event.target.checked)}
                  sx={{ '&.Mui-checked': { color: '#1976D2' }, '&.MuiCheckbox-indeterminate': { color: '#1976D2' } }}
                />
              </TableCell>
              <TableCell align="center" sx={{ width: 80 }}>Action</TableCell>
              <TableCell align="center" sx={{ width: 50 }}>No.</TableCell>
              <TableCell sx={{ width: 120 }}>ECP Code</TableCell>
              <TableCell sx={{ width: 150 }}>Store Name</TableCell>
              <TableCell sx={{ width: 130 }}>Line Name</TableCell>
              <TableCell sx={{ width: 150 }}>Name Customer</TableCell>
              <TableCell sx={{ width: 180 }}>Email</TableCell>
              <TableCell align="center" sx={{ width: 120 }}>Phone Number</TableCell>
              <TableCell align="center" sx={{ width: 110 }}>Line Verification</TableCell>
              <TableCell align="center" sx={{ width: 70 }}>Points</TableCell>
              <TableCell align="center" sx={{ width: 120 }}>Created Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedCustomers.map((customer, index) => (
              <TableRow
                key={customer.id}
                selected={selectedIds.includes(customer.id)}
                sx={{
                  "&.Mui-selected, &.Mui-selected:hover": {
                    backgroundColor: "#F9FAFB",
                  },
                  "& .MuiTableCell-root": {
                    height: 79,
                    borderBottom: `1px solid ${theme.vars.palette.divider}`,
                    fontSize: 12,
                    color: "#25272C",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.includes(customer.id)}
                    onChange={() => handleSelectRow(customer.id)}
                    sx={{ '&.Mui-checked': { color: '#1976D2' } }}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={(event) => handleOpenActionMenu(customer, event)}
                  >
                    <MoreVertOutlined fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell align="center">
                  {filters.page && filters.page_size
                    ? index + 1 + filters.page * filters.page_size
                    : index + 1}
                </TableCell>
                <TableCell>
                  {customer.ecp_code}
                </TableCell>
                <TableCell>
                  {customer.shop_name}
                </TableCell>
                <TableCell>
                  {customer.line_name}
                </TableCell>
                <TableCell>
                  {customer.name} {customer.surname}
                </TableCell>
                <TableCell>
                  {customer.email}
                </TableCell>
                <TableCell align="center">
                  {formatThaiPhone(customer.phone, "-")}
                </TableCell>
                <TableCell align="center">
                  {getVerificationChip(customer.status)}
                </TableCell>
                <TableCell align="center">
                  {customer.points}
                </TableCell>
                <TableCell align="center">
                  {dayjs(customer.created_at).format("DD/MM/YYYY HH:mm")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {filteredCustomers.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRecords}
          rowsPerPage={filters.page_size || 10}
          page={filters.page || 0}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
        />
      )}
    </>
  );

  return (
    <Stack spacing={3}>
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexWrap: { xs: "wrap", md: "nowrap" },
            gap: 1.5,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ flexShrink: 1, minWidth: 0 }}
          >
            <TextField
              size="small"
              placeholder="Search"
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{
                minWidth: 120,
                maxWidth: 180,
                "& .MuiInputBase-root": {
                  height: 40,
                },
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchSharp
                        sx={{ color: "text.secondary", fontSize: 20 }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              select
              size="small"
              value={filters.month}
              onChange={(e) => handleMonthChange(e.target.value)}
              sx={{
                minWidth: 100,
                maxWidth: 150,
                "& .MuiInputBase-root": {
                  height: 40,
                },
                "& .MuiSelect-select": {
                  color:
                    filters.month === "all" ? "text.secondary" : "text.primary",
                },
              }}
            >
              {monthOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              value={filters.year}
              onChange={(e) => handleYearChange(e.target.value)}
              sx={{
                minWidth: 80,
                maxWidth: 120,
                "& .MuiInputBase-root": {
                  height: 40,
                },
              }}
            >
              {yearOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              value={filters.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              SelectProps={{
                renderValue: (value) =>
                  renderStatusFilterOption(
                    value as CustomerListFilters["status"],
                  ),
              }}
              sx={{
                minWidth: 120,
                maxWidth: 180,
                "& .MuiInputBase-root": {
                  height: 40,
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  minHeight: "24px !important",
                  py: "0 !important",
                  color:
                    filters.status === "all"
                      ? "text.secondary"
                      : "text.primary",
                },
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {renderStatusFilterOption(option.value)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {selectedCount > 0 ? (
            <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<DeleteOutlined />}
                disabled={isSubmitting}
                onClick={handleBulkDelete}
                sx={{
                  height: 40,
                  borderRadius: "96px",
                  borderColor: "#BE2217",
                  color: "#BE2217",
                  fontWeight: 700,
                  fontSize: 14,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    borderColor: "#A31B12",
                    bgcolor: "rgba(190,34,23,0.05)",
                  },
                }}
              >
                Delete ({selectedCount})
              </Button>
              {canReject && (
                <Button
                  variant="contained"
                  startIcon={<CancelRounded />}
                  disabled={isSubmitting}
                  onClick={handleBulkReject}
                  sx={{
                    height: 40,
                    borderRadius: "96px",
                    bgcolor: "#BE2217",
                    color: "#FFF",
                    fontWeight: 700,
                    fontSize: 14,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      bgcolor: "#A31B12",
                    },
                  }}
                >
                  Reject ({selectedCount})
                </Button>
              )}
              {canApprove && (
                <Chip
                  clickable
                  disabled={isSubmitting}
                  onClick={handleBulkApprove}
                  label={
                    <Typography
                      component="span"
                      sx={{ fontSize: 16, lineHeight: 1.5, fontWeight: 700 }}
                    >
                      Approve{" "}
                      <Typography
                        component="span"
                        sx={{ fontSize: 16, lineHeight: 1.4, fontWeight: 400 }}
                      >
                        ({selectedCount})
                      </Typography>
                    </Typography>
                  }
                  sx={{
                    px: 2,
                    height: 40,
                    borderRadius: "100px",
                    bgcolor: "#D4EDFF",
                    color: "#0054FC",
                  }}
                />
              )}
            </Stack>
          ) : (
            <Button
              variant="contained"
              startIcon={<CloudUploadOutlined />}
              onClick={handleOpenImportDialog}
              sx={{
                flexShrink: 0,
                whiteSpace: "nowrap",
                height: 40,
                borderRadius: 5,
                bgcolor: "#0F1011",
                "&:hover": {
                  bgcolor: "#232427",
                },
              }}
            >
              Import Point Data
            </Button>
          )}
        </Box>
        {isLoading ? (
          <TableLoadingSkeleton columns={CUSTOMERS_SKELETON_COLUMNS} />
        ) : filteredCustomers.length === 0 ? (
          <Box sx={{ borderTop: `1px solid ${theme.vars.palette.divider}` }}>
            <EmptyState variant="customers" />
          </Box>
        ) : (
          renderTable()
        )}
      </Paper>

      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleCloseActionMenu}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              borderRadius: "8px",
              boxShadow:
                "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
              overflow: "hidden",
            },
          },
          list: {
            sx: { p: 0 },
          },
        }}
      >
        <MenuItem
          onClick={handleActionView}
          sx={{
            minHeight: 40,
            px: 3,
            py: 1,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.4,
            color: "#25272C",
            "&:hover": { bgcolor: "#EDEEF1" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
            <Visibility sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="View Detail"
            slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.4 } } }}
          />
        </MenuItem>
        {getChangeStatusOptions(actionRow?.status).map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleActionStatusChange(option.value)}
            sx={{
              minHeight: 40,
              px: 3,
              py: 1,
              gap: 1,
              fontSize: 16,
              fontWeight: 400,
              lineHeight: 1.4,
              color: "#25272C",
              "&:hover": { bgcolor: "#EDEEF1" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
              {option.icon}
            </ListItemIcon>
            <ListItemText
              primary={option.label}
              slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.4 } } }}
            />
          </MenuItem>
        ))}
        {/* <MenuItem
          onClick={handleActionDelete}
          sx={{
            minHeight: 40,
            px: 3,
            py: 1,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.4,
            color: "#25272C",
            "&:hover": { bgcolor: "#EDEEF1" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
            <Delete sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.4 } } }}
          />
        </MenuItem> */}
      </Menu>

      <CustomDialog
        open={dialogOpen}
        title="Edit Customer"
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        content={
          <CustomerForm
            customer={selectedCustomer}
            onSubmit={handleFormSubmit}
            onCancel={() => setDialogOpen(false)}
          />
        }
      />

      <CustomDialog
        open={importDialogOpen}
        title="Import Points Data"
        onClose={handleCloseImportDialog}
        maxWidth="sm"
        titleStyle={{
          fontSize: 20,
          fontWeight: 600,
          lineHeight: 1.4,
          color: "#25272C",
        }}
        content={
          <Stack spacing={2}>
            <Stack spacing={0.5}>
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: 1.4,
                  color: "#8E95A2",
                }}
              >
                You can download a sample file to view the required data format
              </Typography>
              <Stack direction="row" spacing={1}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.4,
                    color: "#0054FC",
                  }}
                  onClick={handleDownloadSampleXlsx}
                >
                  Download sample (.xlsx)
                </Link>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.4,
                    color: "#0054FC",
                  }}
                  onClick={handleDownloadSampleCsv}
                >
                  Download sample (.csv)
                </Link>
              </Stack>
            </Stack>

            <Stack spacing={1}>
              <Typography
                sx={{ fontSize: 16, lineHeight: 1.4, color: "#25272C" }}
              >
                Attach Increase Points File
              </Typography>
              {importFile ? (
                <Box
                  sx={{
                    borderRadius: 1,
                    border: "1px solid #D1D5DB",
                    bgcolor: "#F9FAFB",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <InsertDriveFileOutlined sx={{ color: "#6B7280", fontSize: 24 }} />
                    <Stack spacing={0}>
                      <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#25272C" }}>
                        {importFile.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#8E95A2" }}>
                        {(importFile.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Stack>
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setImportFile(null);
                      if (importFileInputRef.current) {
                        importFileInputRef.current.value = "";
                      }
                    }}
                  >
                    <CloseRounded sx={{ fontSize: 20, color: "#6B7280" }} />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  onDrop={handleDropImportFile}
                  onDragOver={handleDragOverImport}
                  onDragLeave={handleDragLeaveImport}
                  onClick={() => importFileInputRef.current?.click()}
                  sx={{
                    minHeight: 196,
                    borderRadius: 1,
                    border: isDragActive ? "2px dashed #0054FC" : "1px dashed #6B7280",
                    bgcolor: isDragActive ? "#EBF2FF" : "#EDEEF1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#0054FC",
                      bgcolor: "#F5F8FF",
                    },
                  }}
                >
                  <Stack spacing={2} alignItems="center">
                    <CloudUploadOutlined sx={{ fontSize: 40, color: "#9CADB6" }} />
                    <Stack spacing={0.5} alignItems="center">
                      <Typography sx={{ color: "#5F6D78", fontSize: 16 }}>
                        {isDragActive
                          ? "Drop file to upload"
                          : "Drag and drop file here or click to browse"}
                      </Typography>
                      <Typography sx={{ color: "#9CADB6", fontSize: 14 }}>
                        Supports .xlsx, .xls, .csv · Upload up to 100 rows
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              )}
              <input
                ref={importFileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                hidden
                onChange={handleImportFileInputChange}
              />
            </Stack>
            {importError && (
              <Typography sx={{ fontSize: 13, color: "#BE2217" }}>
                {importError}
              </Typography>
            )}
            <Stack direction="row" spacing={2} justifyContent="flex-end" pt={1}>
              <Button
                variant="outlined"
                onClick={handleCloseImportDialog}
                disabled={isImportSubmitting}
                sx={{
                  height: 40,
                  borderRadius: "96px",
                  borderColor: "#111",
                  color: "#111",
                  fontWeight: 700,
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleImportSubmit}
                disabled={!importFile || isImportSubmitting}
                sx={{
                  height: 40,
                  borderRadius: "96px",
                  bgcolor: "#111111",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#232427" },
                }}
              >
                {isImportSubmitting ? "Importing..." : "Import"}
              </Button>
            </Stack>
          </Stack>
        }
      />

      {/* Status Change Confirmation Dialog */}
      <CustomConfirmDialog
        open={approveDialogOpen}
        title={
          confirmMode === "single"
            ? `Change Status to ${(newStatus || "active").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}`
            : `Approve Customers (${selectedIds.length})`
        }
        onClose={() => { setApproveDialogOpen(false); setNewStatus(""); }}
        content={
          <Typography sx={{ fontSize: 16, color: "#25272C" }}>
            {confirmMode === "single" ? (
              <>
                Are you sure you want to change status of{" "}
                <strong>{selectedCustomer?.name}</strong> to{" "}
                <strong>{(newStatus || "active").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</strong>?
              </>
            ) : (
              `Are you sure you want to approve ${selectedIds.length} customer(s)?`
            )}
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => { setApproveDialogOpen(false); setNewStatus(""); }}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmApprove}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#2D6830",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#245626" },
              }}
            >
              Confirm
            </Button>
          </Stack>
        }
      />

      {/* Reject Confirmation Dialog with Remark */}
      <CustomConfirmDialog
        open={rejectDialogOpen}
        title={
          confirmMode === "single"
            ? `Reject Customer`
            : `Reject Customers (${selectedIds.length})`
        }
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectRemark("");
        }}
        content={
          <Stack spacing={1}>
            <Typography sx={{ fontSize: 16, color: "#25272C" }}>
              Details
              <Typography component="span" sx={{ color: "#BE2217" }}>
                *
              </Typography>
            </Typography>
            <TextField
              multiline
              minRows={4}
              fullWidth
              placeholder="Enter reason for rejection..."
              value={rejectRemark}
              onChange={(e) => setRejectRemark(e.target.value)}
              disabled={isSubmitting}
            />
          </Stack>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectRemark("");
              }}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmReject}
              disabled={isSubmitting || !rejectRemark.trim()}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#BE2217",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#A31B12" },
              }}
            >
              Reject
            </Button>
          </Stack>
        }
      />

      {/* Change Status with Remark Dialog */}
      <CustomConfirmDialog
        open={changeStatusRemarkOpen}
        title={`Change Status to ${(newStatus || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}`}
        onClose={() => {
          setChangeStatusRemarkOpen(false);
          setNewStatus("");
          setChangeStatusRemark("");
        }}
        content={
          <Stack spacing={1}>
            <Typography sx={{ fontSize: 16, color: "#25272C" }}>
              Are you sure you want to change status of{" "}
              <strong>{selectedCustomer?.name}</strong> to{" "}
              <strong>{(newStatus || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</strong>?
            </Typography>
            <Typography sx={{ fontSize: 16, color: "#25272C", mt: 1 }}>
              Reason
            </Typography>
            <TextField
              multiline
              minRows={4}
              fullWidth
              placeholder="Enter reason..."
              value={changeStatusRemark}
              onChange={(e) => setChangeStatusRemark(e.target.value)}
              disabled={isSubmitting}
            />
          </Stack>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => {
                setChangeStatusRemarkOpen(false);
                setNewStatus("");
                setChangeStatusRemark("");
              }}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmChangeStatusWithRemark}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#2D6830",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#245626" },
              }}
            >
              Confirm
            </Button>
          </Stack>
        }
      />

      {/* Delete Confirmation Dialog */}
      <CustomConfirmDialog
        open={deleteDialogOpen}
        title={
          confirmMode === "single"
            ? `Delete Customer`
            : `Delete Customers (${selectedIds.length})`
        }
        onClose={() => setDeleteDialogOpen(false)}
        content={
          <Typography sx={{ fontSize: 16, color: "#25272C" }}>
            {confirmMode === "single" ? (
              <>
                Are you sure you want to delete{" "}
                <strong>{selectedCustomer?.name}</strong>? This action cannot be
                undone.
              </>
            ) : (
              `Are you sure you want to delete ${selectedIds.length} customer(s)? This action cannot be undone.`
            )}
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#BE2217",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#A31B12" },
              }}
            >
              Delete
            </Button>
          </Stack>
        }
      />
    </Stack>
  );
}
